import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import api from "@/lib/api";
import { MOCKUPS } from "@/components/mockups/DashboardMockups";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductPage() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    useEffect(() => {
        api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(null));
    }, [slug]);

    if (!product) return <div className="pt-40 text-center text-slate-500">Loading…</div>;
    const Mockup = MOCKUPS[slug] || (() => null);

    return (
        <main data-testid={`product-page-${slug}`} className="pt-[72px] bg-white">
            {/* Hero */}
            <section className="bg-[#0B1930] text-white pt-16 lg:pt-24 pb-20 relative overflow-hidden grain">
                <div className="hero-glow top-[-30%] right-[-10%]" style={{ background: product.accent || "#2563EB" }} />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-px bg-[#E8B923]" />
                            <span className="eyebrow">{product.tagline}</span>
                        </div>
                        <motion.h1 initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="headline text-5xl md:text-6xl lg:text-7xl">
                            {product.name}
                        </motion.h1>
                        <p className="text-white/70 text-lg mt-6 max-w-lg leading-relaxed">{product.long_description}</p>
                        <div className="flex flex-wrap gap-3 mt-8">
                            <Link to="/request-demo" className="btn-gold">Request Demo <ArrowUpRight className="w-4 h-4" /></Link>
                            <a href="#features" className="btn-ghost-navy">See Features</a>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <Mockup />
                    </div>
                </div>
            </section>

            {/* Problem / Solution */}
            <section className="bg-[#F7F9FC] py-24">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
                    <div>
                        <div className="eyebrow eyebrow--slate">The Problem</div>
                        <h2 className="headline text-3xl lg:text-4xl mt-4 text-navy">{product.problem}</h2>
                    </div>
                    <div>
                        <div className="eyebrow">How Colecle Solves It</div>
                        <h2 className="headline text-3xl lg:text-4xl mt-4 text-navy">{product.solution}</h2>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-24">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="eyebrow">Key Features</div>
                    <h2 className="headline text-4xl lg:text-5xl mt-4 text-navy mb-14">Built for enterprise operations.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(product.features || []).map((f, i) => (
                            <motion.div key={f} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.05 }} className="bg-[#F7F9FC] border border-slate-200 rounded-xl p-6 flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-navy text-[#E8B923] flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Check className="w-3.5 h-3.5" />
                                </div>
                                <span className="text-navy text-sm">{f}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Workflow */}
            <section className="bg-[#0B1930] text-white py-24">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="eyebrow">How It Works</div>
                    <h2 className="headline text-4xl lg:text-5xl mt-4 mb-14">Workflow</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {(product.workflow || []).map((w, i) => (
                            <motion.div key={w} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="border-t border-white/20 pt-4">
                                <span className="font-mono text-[10px] text-white/40 tracking-widest">STEP {String(i + 1).padStart(2, "0")}</span>
                                <p className="font-serif text-lg mt-2">{w}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-24">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="eyebrow">Benefits</div>
                        <h2 className="headline text-4xl lg:text-5xl mt-4 text-navy">Outcomes that matter.</h2>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                        {(product.benefits || []).map((b, i) => (
                            <div key={b} className="bg-white border border-slate-200 rounded-xl p-6">
                                <div className="font-mono text-[10px] text-slate-400 tracking-widest">BENEFIT · {String(i + 1).padStart(2, "0")}</div>
                                <p className="font-serif text-xl text-navy mt-3">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#F7F9FC] py-24">
                <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
                    <div className="eyebrow">FAQ</div>
                    <h2 className="headline text-4xl lg:text-5xl mt-4 text-navy mb-10">Frequently asked</h2>
                    <Accordion type="single" collapsible className="w-full">
                        {[
                            { q: `What deployment options are available for ${product.name}?`, a: "Colecle Cloud (managed SaaS), Private Cloud on your preferred provider, or air-gapped on-premises for regulated environments." },
                            { q: "How is data secured?", a: "AES-256 encryption at rest, TLS 1.3 in transit, cryptographic audit trails, and role-based access control. ISO 27001 and SOC 2 aligned controls." },
                            { q: "Can we integrate with existing systems?", a: "Yes — REST APIs, SSO (SAML/OIDC/LDAP/AD), and pre-built connectors are available. Custom integrations are supported on Business and Enterprise plans." },
                            { q: "What support is included?", a: "Community support on Free, email support on Professional, priority support on Business, and 24/7 dedicated support on Enterprise." },
                        ].map((it, i) => (
                            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                                <AccordionTrigger className="text-left font-serif text-lg text-navy">{it.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed">{it.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-navy text-white py-24">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                    <h2 className="headline text-4xl lg:text-5xl">Ready to see {product.name}?</h2>
                    <Link to="/request-demo" className="btn-gold">Request a Demo <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
            </section>
        </main>
    );
}
